// client/src/components/ProductHobbyDetails.js
// The hobby-specific parts of a product page.
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { AuthContext } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/media';
import { BUILD_TYPE_HELP, BUILD_TYPE_LABELS, getMyModels, specRows, splitList, youtubeEmbedUrl } from '../utils/productSpecs';

export const ProductVideo = ({ url, title }) => {
  const embed = youtubeEmbedUrl(url);
  if (!embed) return null;
  return (
    <Box sx={{ mt: 2, aspectRatio: '16 / 9', bgcolor: 'action.hover' }}>
      <Box
        component="iframe"
        src={embed}
        title={`Video: ${title}`}
        loading="lazy"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </Box>
  );
};

// Condition and history for a second-hand item.
export const PreOwnedDetails = ({ product }) => {
  if (!product.condition_grade && !product.usage_notes && !product.repair_history) return null;
  return (
    <Box sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Pre-owned</Typography>
        {product.condition_grade && <Chip label={product.condition_grade} size="small" color="primary" />}
      </Stack>
      {product.usage_notes && (
        <Typography variant="body2"><strong>Use so far:</strong> {product.usage_notes}</Typography>
      )}
      {product.repair_history && (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}><strong>Crash and repair history:</strong> {product.repair_history}</Typography>
      )}
    </Box>
  );
};

// Whether this fits a model the shopper saved, then which models it fits.
export const CompatibilityNotice = ({ product }) => {
  const fits = splitList(product.compatible_with);
  if (fits.length === 0) return null;
  const mine = getMyModels().filter((model) => fits.some((fit) => fit.toLowerCase() === model.toLowerCase()));
  return (
    <Box sx={{ mb: 2 }}>
      {mine.length > 0 && (
        <Alert icon={<CheckCircleOutlineIcon fontSize="inherit" />} severity="success" sx={{ mb: 1 }}>
          Fits your {mine.join(', ')}
        </Alert>
      )}
      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>Fits these models</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {fits.map((model) => (
          <Chip key={model} component={Link} to={`/?${new URLSearchParams({ fits: model })}`} clickable label={model} size="small" variant="outlined" />
        ))}
      </Stack>
    </Box>
  );
};

// What the buyer still needs before it will run.
export const AlsoNeeds = ({ product }) => {
  const needs = splitList(product.also_needs);
  if (needs.length === 0 && !BUILD_TYPE_HELP[product.build_type]) return null;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      {BUILD_TYPE_HELP[product.build_type] && (
        <Box><strong>{BUILD_TYPE_LABELS[product.build_type]}:</strong> {BUILD_TYPE_HELP[product.build_type]}</Box>
      )}
      {needs.length > 0 && <Box sx={{ mt: BUILD_TYPE_HELP[product.build_type] ? 0.5 : 0 }}><strong>You'll also need:</strong> {needs.join(', ')}</Box>}
    </Alert>
  );
};

export const SpecList = ({ product }) => {
  const rows = specRows(product);
  if (rows.length === 0) return null;
  return (
    <Box component="dl" sx={{ m: 0, mb: 2, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 3, rowGap: 0.5 }}>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
          <Typography component="dd" variant="body2" fontWeight={600} sx={{ m: 0 }}>{value}</Typography>
        </React.Fragment>
      ))}
    </Box>
  );
};

export const BackInStockForm = ({ productId }) => {
  const { user } = useContext(AuthContext);
  const [email, setEmail] = useState(user?.email || '');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}/notify-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (done) return <Alert severity="success">We'll email {email} once when this is back in stock.</Alert>;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Out of stock. Want to know when it's back?</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Stack direction="row" spacing={1}>
        <TextField size="small" type="email" label="Your email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
        <Button type="submit" variant="contained" disabled={saving} startIcon={<NotificationsActiveOutlinedIcon />} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          Notify me
        </Button>
      </Stack>
    </Box>
  );
};
